import React, { useReducer, useMemo, useEffect } from 'react';
import { CheckCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import { Button, Radio, RadioGroup, Checkbox, Card, CardBody } from '@heroui/react';
import { QuizQuestion, QuestionConfig, QuestionOption } from '@/services/apis/guides';
import { useLanguage } from '@/contexts/LanguageContext';
import GuidesApi from '@/services/apis/guides';

interface QuizStepProps {
  content?: {
    questions?: QuizQuestion[];
  };
  title?: string;
  stepId: string;
  guideId: string;
  questionConfig?: QuestionConfig; // From step level (applies to all translations)
  onComplete?: () => void;
  savedScore?: number; // Score saved in guide_user_progress
}

interface QuizState {
  selectedAnswers: Record<string, number | number[]>;
  currentQuestionIndex: number;
  score?: number;
  isCompleted: boolean;
  isSubmitting: boolean;
}

type QuizAction =
  | { type: 'SELECT_ANSWER'; questionId: string; optionIndex: number; isChecked: boolean; allowMultiple: boolean }
  | { type: 'NEXT_QUESTION' }
  | { type: 'PREVIOUS_QUESTION' }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS'; score: number }
  | { type: 'SUBMIT_ERROR' }
  | { type: 'RESET' }
  | { type: 'RESET_ON_LANGUAGE_CHANGE' };

// Helper function to shuffle array using Fisher-Yates algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const createInitialQuizState = (savedScore?: number): QuizState => ({
  selectedAnswers: {},
  currentQuestionIndex: 0,
  score: savedScore,
  isCompleted: savedScore !== undefined && savedScore !== null,
  isSubmitting: false,
});

const quizReducer = (state: QuizState, action: QuizAction): QuizState => {
  switch (action.type) {
    case 'SELECT_ANSWER': {
      if (state.isCompleted) return state;

      const { questionId, optionIndex, isChecked, allowMultiple } = action;
      const currentAnswer = state.selectedAnswers[questionId];

      let newAnswer: number | number[];

      if (allowMultiple) {
        // Multiple choice
        const currentArray = Array.isArray(currentAnswer)
          ? currentAnswer
          : currentAnswer !== undefined
          ? [currentAnswer]
          : [];
        if (isChecked) {
          newAnswer = [...currentArray, optionIndex];
        } else {
          newAnswer = currentArray.filter((idx) => idx !== optionIndex);
        }
      } else {
        // Single choice
        newAnswer = isChecked ? optionIndex : (undefined as any);
      }

      return {
        ...state,
        selectedAnswers: {
          ...state.selectedAnswers,
          [questionId]: newAnswer,
        },
      };
    }

    case 'NEXT_QUESTION':
      return {
        ...state,
        currentQuestionIndex: state.currentQuestionIndex + 1,
      };

    case 'PREVIOUS_QUESTION':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, state.currentQuestionIndex - 1),
      };

    case 'SUBMIT_START':
      return {
        ...state,
        isSubmitting: true,
      };

    case 'SUBMIT_SUCCESS':
      return {
        ...state,
        score: action.score,
        isCompleted: true,
        isSubmitting: false,
      };

    case 'SUBMIT_ERROR':
      return {
        ...state,
        isSubmitting: false,
      };

    case 'RESET':
      return {
        selectedAnswers: {},
        currentQuestionIndex: 0,
        isCompleted: false,
        isSubmitting: false,
      };

    case 'RESET_ON_LANGUAGE_CHANGE':
      return {
        selectedAnswers: state.selectedAnswers,
        currentQuestionIndex: 0,
        isCompleted: false,
        isSubmitting: false,
        score: state.score,
      };

    default:
      return state;
  }
};

const QuizStep: React.FC<QuizStepProps> = ({
  content,
  title: _title,
  stepId,
  guideId,
  questionConfig,
  onComplete,
  savedScore,
}) => {
  const { currentLanguage } = useLanguage();
  const questions = content?.questions || [];
  const config: QuestionConfig = questionConfig || { minimumScore: 60 };
  // Ensure minimumScore is a valid number between 0 and 100, default to 60
  const minimumScore =
    typeof config.minimumScore === 'number' && config.minimumScore >= 0 && config.minimumScore <= 100
      ? config.minimumScore
      : 60;

  // Helper function to compute displayed questions with proper randomization
  const computeDisplayedQuestions = (questionsToProcess: QuizQuestion[]): QuizQuestion[] => {
    if (questionsToProcess.length === 0) return [];

    let questionsToDisplay = [...questionsToProcess];

    // Step 1: Apply random selection if configured
    // If selectRandomCount is set and is less than total questions, randomly select that many
    if (config.selectRandomCount && typeof config.selectRandomCount === 'number' && config.selectRandomCount > 0) {
      if (config.selectRandomCount < questionsToProcess.length) {
        // Shuffle and take the first N questions
        const shuffled = shuffleArray(questionsToProcess);
        questionsToDisplay = shuffled.slice(0, config.selectRandomCount);
      }
      // If selectRandomCount >= total questions, use all questions
    }

    // Step 2: Apply random order if configured
    // This randomizes the order of the selected questions
    if (config.randomizeOrder) {
      questionsToDisplay = shuffleArray(questionsToDisplay);
    }

    return questionsToDisplay;
  };

  // Initialize displayed questions based on config
  const displayedQuestions = useMemo(() => {
    return computeDisplayedQuestions(questions);
  }, [questions, config.selectRandomCount, config.randomizeOrder, config]);

  // Initialize quiz state with saved score if available
  const [quizState, dispatch] = useReducer(quizReducer, createInitialQuizState(savedScore));

  // Update quiz state when savedScore changes (e.g., when progress is loaded)
  // This handles the case where progress loads after component mounts
  useEffect(() => {
    if (savedScore !== undefined && savedScore !== null && !quizState.isCompleted) {
      // Only update if quiz is not already completed (to avoid overriding user's current session)
      dispatch({ type: 'SUBMIT_SUCCESS', score: savedScore });
    }
  }, [savedScore]);

  // Reset quiz state when language changes
  useEffect(() => {
    dispatch({ type: 'RESET_ON_LANGUAGE_CHANGE' });
  }, [currentLanguage]);

  const handleAnswerChange = (questionId: string, optionIndex: number, isChecked: boolean) => {
    const question = displayedQuestions.find((q) => q.id === questionId);
    if (!question) return;

    dispatch({
      type: 'SELECT_ANSWER',
      questionId,
      optionIndex,
      isChecked,
      allowMultiple: question.allowMultiple,
    });
  };

  const handleSubmit = async () => {
    if (displayedQuestions.length === 0) return;

    dispatch({ type: 'SUBMIT_START' });

    try {
      // Get displayed question IDs for backend to filter correctly
      const displayedQuestionIds = displayedQuestions.map((q) => q.id);

      // Submit answers to backend for score computation
      const response = await GuidesApi.submitQuizAnswers(
        guideId,
        stepId,
        quizState.selectedAnswers,
        displayedQuestionIds,
      );

      const score = response.data?.score || 0;
      const serverMinimumScore = response.data?.minimumScore || minimumScore;

      // Calculate if quiz passed: score must be >= minimumScore
      const isPassed = score >= serverMinimumScore;

      dispatch({ type: 'SUBMIT_SUCCESS', score });

      // Auto-complete step ONLY if passed (score >= minimumScore)
      // CRITICAL: Only call onComplete when score >= minimumScore
      // Do NOT call onComplete if score < minimumScore
      if (isPassed && score >= serverMinimumScore && onComplete) {
        onComplete();
      }
      // Explicitly do nothing if not passed - step should remain incomplete
    } catch (error) {
      console.error('Error submitting quiz answers:', error);
      dispatch({ type: 'SUBMIT_ERROR' });
      // TODO: Show error message to user
    }
  };

  const handleRetry = () => {
    dispatch({ type: 'RESET' });
  };

  const handleNext = () => {
    if (quizState.currentQuestionIndex < displayedQuestions.length - 1) {
      dispatch({ type: 'NEXT_QUESTION' });
    }
  };

  const handlePrevious = () => {
    if (quizState.currentQuestionIndex > 0) {
      dispatch({ type: 'PREVIOUS_QUESTION' });
    }
  };

  // Always use computed displayedQuestions (recomputed based on config)
  const questionsToDisplay = displayedQuestions;

  if (questionsToDisplay.length === 0) {
    return <div className="text-center text-gray-500 dark:text-gray-400 py-8">No questions available</div>;
  }

  // Show results if completed
  if (quizState.score !== undefined) {
    const isPassed = (quizState.score ?? 0) >= minimumScore;

    return (
      <div className="text-center space-y-6">
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${
            isPassed ? 'bg-green-100 dark:bg-green-900/20' : 'bg-yellow-100 dark:bg-yellow-900/20'
          }`}
        >
          {isPassed ? (
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
          ) : (
            <AlertTriangle className="w-10 h-10 text-yellow-600 dark:text-yellow-400" />
          )}
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {isPassed ? 'Quiz Passed!' : 'Quiz Not Passed'}
          </h3>
          <p
            className={`text-4xl font-bold mb-2 ${
              isPassed ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
            }`}
          >
            {quizState.score}%
          </p>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You scored {quizState.score}% on this quiz
            {!isPassed && ` (Minimum required: ${minimumScore}%)`}
          </p>
          {!isPassed && (
            <Button
              color="warning"
              variant="solid"
              onPress={handleRetry}
              startContent={<RotateCcw className="w-4 h-4" />}
            >
              Retry Quiz
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Show question one at a time
  const currentQuestion = questionsToDisplay[quizState.currentQuestionIndex];
  const currentAnswer = quizState.selectedAnswers[currentQuestion.id];
  const isLastQuestion = quizState.currentQuestionIndex === questionsToDisplay.length - 1;
  const isFirstQuestion = quizState.currentQuestionIndex === 0;
  const allQuestionsAnswered = questionsToDisplay.every(
    (q) => quizState.selectedAnswers[q.id] !== undefined && quizState.selectedAnswers[q.id] !== null,
  );

  return (
    <div className="space-y-6">
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardBody className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              Question {quizState.currentQuestionIndex + 1} of {questionsToDisplay.length}
            </h4>
          </div>

          <h5 className="text-xl font-medium text-gray-900 dark:text-white mb-6">{currentQuestion.title}</h5>

          {currentQuestion.allowMultiple ? (
            <div className="space-y-3">
              {currentQuestion.options.map((option: QuestionOption, optionIndex: number) => {
                const isSelected = Array.isArray(currentAnswer)
                  ? currentAnswer.includes(optionIndex)
                  : currentAnswer === optionIndex;

                return (
                  <Checkbox
                    key={optionIndex}
                    isSelected={isSelected}
                    onValueChange={(checked) => handleAnswerChange(currentQuestion.id, optionIndex, checked)}
                    className="mb-2"
                  >
                    {option.text}
                  </Checkbox>
                );
              })}
            </div>
          ) : (
            <RadioGroup
              key={currentQuestion.id}
              value={
                currentAnswer !== undefined && currentAnswer !== null
                  ? Array.isArray(currentAnswer)
                    ? currentAnswer[0]?.toString()
                    : currentAnswer.toString()
                  : undefined
              }
              onValueChange={(value) => handleAnswerChange(currentQuestion.id, parseInt(value, 10), true)}
            >
              {currentQuestion.options.map((option: QuestionOption, optionIndex: number) => (
                <Radio key={`${currentQuestion.id}-${optionIndex}`} value={optionIndex.toString()} className="mb-2">
                  {option.text}
                </Radio>
              ))}
            </RadioGroup>
          )}
        </CardBody>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="flat" onPress={handlePrevious} isDisabled={isFirstQuestion}>
          Previous
        </Button>

        <div className="flex gap-2">
          {!isLastQuestion ? (
            <Button color="primary" variant="solid" onPress={handleNext}>
              Next
            </Button>
          ) : (
            <Button
              color="primary"
              variant="solid"
              onPress={handleSubmit}
              isDisabled={!allQuestionsAnswered || quizState.isSubmitting}
              isLoading={quizState.isSubmitting}
            >
              {quizState.isSubmitting ? 'Submitting...' : 'Submit Quiz'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizStep;
